import React, { useState } from "react";
import axios from "axios";
import { Mail, Phone, Linkedin, ArrowUpRight, Loader2 } from "lucide-react";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const Contact = () => {
  const [form, setForm] = useState({
    name: "",
    email: "",
    phone: "",
    interest: "",
    message: "",
  });
  const [submitting, setSubmitting] = useState(false);

  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.phone || !form.message) {
      toast.error("Please fill in all required fields");
      return;
    }
    setSubmitting(true);
    try {
      const res = await axios.post(`${API}/contact`, form);
      toast.success(res.data.message || "Message sent");
      setForm({ name: "", email: "", phone: "", interest: "", message: "" });
    } catch (err) {
      const detail =
        err?.response?.data?.detail || "Could not send. Please try again.";
      toast.error(typeof detail === "string" ? detail : "Something went wrong");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section
      id="contact"
      data-testid="contact-section"
      className="bg-black text-white"
    >
      <div className="mx-auto max-w-7xl px-5 md:px-10 py-20 md:py-28">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12">
          <div className="lg:col-span-5">
            <div className="overline text-white/70 mb-3">Contact</div>
            <h2 className="font-display text-4xl md:text-5xl lg:text-6xl leading-[0.98]">
              Let&apos;s work
              <br />
              <span className="bg-[color:var(--bts-yellow)] text-black px-2 border border-[color:var(--bts-yellow)]">
                together
              </span>
              .
            </h2>
            <p className="mt-6 text-white/70 max-w-md">
              Tell us about your team, campus, or personal goal. We&apos;ll get back
              within a business day.
            </p>

            <div className="mt-10 space-y-5">
              <ContactRow
                icon={<Mail size={18} />}
                label="Email"
                value="ruchika.thakkar1@gmail.com"
                href="mailto:ruchika.thakkar1@gmail.com"
                testid="contact-info-email"
              />
              <ContactRow
                icon={<Phone size={18} />}
                label="Phone"
                value="+91 99601 64633"
                href="tel:+919960164633"
                testid="contact-info-phone"
              />
              <ContactRow
                icon={<Linkedin size={18} />}
                label="LinkedIn"
                value="ruchika-thakkar-singh"
                href="https://www.linkedin.com/in/ruchika-thakkar-singh/"
                testid="contact-info-linkedin"
              />
            </div>
          </div>

          <form
            onSubmit={submit}
            data-testid="contact-form"
            className="lg:col-span-7 bg-white text-black border border-[color:var(--bts-yellow)] p-7 md:p-10"
          >
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <Field
                label="Name *"
                testid="contact-input-name"
                value={form.name}
                onChange={(v) => update("name", v)}
                placeholder="Your full name"
              />
              <Field
                label="Email *"
                type="email"
                testid="contact-input-email"
                value={form.email}
                onChange={(v) => update("email", v)}
                placeholder="you@company.com"
              />
              <Field
                label="Phone *"
                testid="contact-input-phone"
                value={form.phone}
                onChange={(v) => update("phone", v)}
                placeholder="+91 …"
              />
              <div>
                <label className="overline block mb-2">Interested in</label>
                <select
                  data-testid="contact-input-interest"
                  value={form.interest}
                  onChange={(e) => update("interest", e.target.value)}
                  className="w-full border-b border-black bg-transparent py-2 focus:outline-none text-sm"
                >
                  <option value="">Select a topic</option>
                  <option>Communication Workshop</option>
                  <option>Grooming & Etiquette Workshop</option>
                  <option>Interview Prep</option>
                  <option>Corporate Behavioural Training</option>
                  <option>POSH Training</option>
                  <option>NLP Coaching</option>
                  <option>Educational Institute Workshop</option>
                </select>
              </div>
            </div>

            <div className="mt-6">
              <label className="overline block mb-2">Message *</label>
              <textarea
                data-testid="contact-input-message"
                value={form.message}
                onChange={(e) => update("message", e.target.value)}
                rows={5}
                placeholder="Tell us a bit about your team, goals, and timeline."
                className="w-full border border-black p-3 bg-white text-sm focus:outline-none"
              />
            </div>

            <button
              type="submit"
              data-testid="contact-submit"
              disabled={submitting}
              className="btn-primary mt-8"
            >
              {submitting ? (
                <>
                  <Loader2 size={16} className="animate-spin" /> Sending
                </>
              ) : (
                <>
                  Send message <ArrowUpRight size={16} />
                </>
              )}
            </button>

            <p className="text-[11px] text-neutral-500 mt-4">
              By submitting, you consent to being contacted about your enquiry.
            </p>
          </form>
        </div>
      </div>
    </section>
  );
};

const Field = ({ label, testid, value, onChange, type = "text", placeholder }) => (
  <div>
    <label className="overline block mb-2">{label}</label>
    <input
      data-testid={testid}
      type={type}
      value={value}
      onChange={(e) => onChange(e.target.value)}
      placeholder={placeholder}
      className="w-full border-b border-black bg-transparent py-2 focus:outline-none text-sm placeholder:text-neutral-400"
    />
  </div>
);

const ContactRow = ({ icon, label, value, href, testid }) => (
  <a
    href={href}
    target={href?.startsWith("http") ? "_blank" : undefined}
    rel="noreferrer"
    data-testid={testid}
    className="flex items-center gap-4 group"
  >
    <span className="w-10 h-10 border border-white/30 flex items-center justify-center group-hover:bg-[color:var(--bts-yellow)] group-hover:text-black group-hover:border-[color:var(--bts-yellow)] transition-colors">
      {icon}
    </span>
    <span>
      <span className="block overline text-white/50">{label}</span>
      <span className="block font-semibold">{value}</span>
    </span>
  </a>
);

export default Contact;
